import React, { useState, useEffect } from 'react';
import { Modal, Table, Button, Space, message, Select, Card, Row, Col, Popconfirm } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { AdvSet, AdvSetWithAdvs, AdvWithClient } from '../../types/adv';
import { advSetService } from '../../services/advSetService';
import { advService } from '../../services/advService';

interface AdvSetManagementProps {
  set?: AdvSet;
  isVisible: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const AdvSetManagement: React.FC<AdvSetManagementProps> = ({
  set,
  isVisible,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [setWithAdvs, setSetWithAdvs] = useState<AdvSetWithAdvs | null>(null);
  const [allAdvs, setAllAdvs] = useState<AdvWithClient[]>([]);
  const [selectedAdvId, setSelectedAdvId] = useState<number | null>(null);

  // Fetch set details with advertisements
  const fetchSetDetails = async () => {
    if (!set) return;
    
    setLoading(true);
    try {
      const data = await advSetService.getAdvSetById(set.id);
      setSetWithAdvs(data);
    } catch (error) {
      console.error('Error fetching set details:', error);
      message.error(t('advertising.sets.notifications.error', { message: (error as Error).message }));
    } finally {
      setLoading(false);
    }
  };

  // Fetch all advertisements for adding to set
  const fetchAllAdvs = async () => {
    try {
      const data = await advService.getAllAdvs();
      setAllAdvs(data);
    } catch (error) {
      console.error('Error fetching advertisements:', error);
      message.error(t('advertising.advs.notifications.error', { message: (error as Error).message }));
    }
  };

  useEffect(() => {
    if (isVisible && set) {
      fetchSetDetails();
      fetchAllAdvs();
    }
  }, [isVisible, set]);

  // Add advertisement to set
  const handleAddAdv = async () => {
    if (!set || !selectedAdvId) return;

    try {
      await advSetService.addAdvToSet(set.id, selectedAdvId);
      message.success(t('advertising.sets.notifications.adv_added'));
      setSelectedAdvId(null);
      fetchSetDetails();
    } catch (error) {
      console.error('Error adding advertisement to set:', error);
      message.error(t('advertising.sets.notifications.error', { message: (error as Error).message }));
    }
  };

  // Remove advertisement from set
  const handleRemoveAdv = async (advId: number) => {
    if (!set) return;

    try {
      await advSetService.removeAdvFromSet(set.id, advId);
      message.success(t('advertising.sets.notifications.adv_removed'));
      fetchSetDetails();
    } catch (error) {
      console.error('Error removing advertisement from set:', error);
      message.error(t('advertising.sets.notifications.error', { message: (error as Error).message }));
    }
  };

  // Get available advertisements (not already in the set)
  const getAvailableAdvs = () => {
    if (!setWithAdvs || !allAdvs) return [];
    
    // Ensure setWithAdvs.advs is an array before calling map
    const setAdvs = setWithAdvs.advs || [];
    const setAdvIds = setAdvs.map(adv => adv.id);
    return allAdvs.filter(adv => !setAdvIds.includes(adv.id));
  };

  // Table columns for advertisements in the set
  const columns = [
    {
      title: t('advertising.advs.list.name'),
      dataIndex: 'adv_name',
      key: 'adv_name',
    },
    {
      title: t('advertising.advs.list.client'),
      dataIndex: 'client_name',
      key: 'client_name',
    },
    {
      title: t('advertising.sets.management.actions'),
      key: 'actions',
      render: (_: any, record: AdvWithClient) => (
        <Popconfirm
          title={t('advertising.sets.management.confirm_remove')}
          onConfirm={() => handleRemoveAdv(record.id)}
          okText="Yes"
          cancelText="No"
        >
          <Button icon={<DeleteOutlined />} danger size="small">
            {t('advertising.sets.management.remove')}
          </Button>
        </Popconfirm>
      ),
    },
  ];

  const availableAdvs = getAvailableAdvs();

  return (
    <Modal
      title={t('advertising.sets.management.title', { name: set?.adv_set_name })}
      open={isVisible}
      onCancel={onClose}
      footer={null}
      width={800}
      destroyOnClose={true}
    >
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        {/* Add Advertisement Section */}
        <Card title={t('advertising.sets.management.add_section')} size="small">
          <Row gutter={16} align="middle">
            <Col flex={1}>
              <Select
                placeholder={t('advertising.sets.management.select_adv')}
                value={selectedAdvId}
                onChange={setSelectedAdvId}
                style={{ width: '100%' }}
                options={availableAdvs.map(adv => ({
                  label: `${adv.adv_name} (${adv.client_name})`,
                  value: adv.id,
                }))}
                showSearch
                filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
              />
            </Col>
            <Col>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAddAdv}
                disabled={!selectedAdvId}
              >
                {t('advertising.sets.management.add')}
              </Button>
            </Col>
          </Row>
        </Card>

        {/* Current Advertisements Table */}
        <Card title={t('advertising.sets.management.current_advs')} size="small">
          <Table
            columns={columns}
            dataSource={setWithAdvs?.advs || []}
            rowKey="id"
            loading={loading}
            pagination={false}
            size="small"
          />
        </Card>
      </Space>
    </Modal>
  );
};

export default AdvSetManagement; 